import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';

import fetchEndpoint from '../../services/fetchEndpoint';
import { userAppContext } from '../../context/Context';

import GeneGeneralInfo from './GeneGeneralInfo';
import GeneSynonyms from './GeneSynonyms';
import PathwayDiagram from './PathwayDiagram';
import ErrorMessage from '../UI/ErrorMessage';
import SharedButton from '@/global/Button/Button';

const noAccessTitle = <span>This content is not accessible to your current profile</span>;
const noAccessSubTitle = (
  <span>
    If you want to upgrade your account, please contact us at{' '}
    <a href="mailto:info@disgenet.com">info@disgenet.com</a>
  </span>
);

const resetGeneInfo = {
  symbol: '',
  id: null,
  name: '',
};

interface GeneContentProps {
  geneInfo: {
    symbol: string;
    id: string | null;
    name?: string;
  };
  setGeneContent: (content: any) => void;
  isActive: boolean;
}

export default function GeneContent({ geneInfo, setGeneContent, isActive }: GeneContentProps) {
  const navigate = useNavigate();
  const [geneDetailInfo, setGeneDetailInfo] = useState<any>(null);
  const [tabIndex, setTabIndex] = useState(0);
  const { symbol, id } = geneInfo;
  const {
    authorization,
    setAuthorization,
    isAuthenticated,
    setIsAuthenticated,
    setGroups,
    setAuthError,
    restrictPathwayDisplay,
  } = userAppContext();

  useEffect(() => {
    const unauth = isAuthenticated ? '' : '?skey=unauth';

    if (id) {
      const endpoint = `api/gene/detail/${id}${unauth}`;
      fetchEndpoint(endpoint, authorization, setIsAuthenticated).then(response => {
        if (response?.status === 'OK') {
          setGeneDetailInfo(response.payload);
        }
      });
    }
  }, [geneInfo]);

  return (
    <Tabs selectedIndex={tabIndex} onSelect={index => setTabIndex(index)}>
      <TabList className="menuTabs">
        <Tab className="react-tabs__tab">General Info</Tab>
        <Tab className="react-tabs__tab">Synonyms</Tab>
        <Tab className="react-tabs__tab">Pathways</Tab>
      </TabList>

      <TabPanel className="tab-pane p-2">
        {geneDetailInfo && <GeneGeneralInfo geneDetailInfo={geneDetailInfo} />}
      </TabPanel>

      <TabPanel className="tab-pane p-2">
        {geneDetailInfo && <GeneSynonyms geneDetailInfo={geneDetailInfo} />}
      </TabPanel>

      <TabPanel className="tab-pane p-2">
        {restrictPathwayDisplay ? (
          <ErrorMessage
            title="This content is not available with your current plan"
            subtitle={
              <div className="text-center">
                <p>Upgrade your plan to access this feature.</p>
                <SharedButton variant="gradient" onClick={() => navigate('/academic-plans')}>
                  Upgrade your plan
                </SharedButton>
              </div>
            }
          />
        ) : (
          <PathwayDiagram geneSymbol={symbol} />
        )}
      </TabPanel>
    </Tabs>
  );
}
